'use client'

import { useState } from 'react'
import { MdClose, MdFlag } from 'react-icons/md'

interface Props {
    /** Rótulo do que está sendo alterado ("procedimento", "exame", "consulta"). */
    entityLabel: string
    /** Status atual, para marcar na lista e bloquear o salvar sem mudança. */
    currentStatus: string
    /** Opções na ordem em que devem aparecer. `CANCELLED` não deve entrar aqui. */
    options: { value: string; label: string }[]
    saving?: boolean
    onClose: () => void
    onConfirm: (status: string) => void
}

/**
 * Troca manual de status de um atendimento.
 *
 * Existe pela importação do sistema antigo: muito atendimento veio marcado como
 * concluído sem ter acontecido, e não havia como devolvê-lo ao estado real. Quem
 * sabe o que aconteceu é o operador.
 *
 * Cancelar NÃO entra aqui de propósito: cancelamento mexe em cobrança, estorno e
 * agenda, e tem ação própria — o backend recusa `CANCELLED` por esta via.
 */
export default function ChangeStatusModal({
    entityLabel,
    currentStatus,
    options,
    saving = false,
    onClose,
    onConfirm,
}: Props) {
    const [selected, setSelected] = useState(currentStatus)

    return (
        <div className="modal modal-open" style={{ zIndex: 9999 }}>
            <div className="modal-box max-w-md">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <MdFlag /> Alterar status
                    </h3>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </div>
                <p className="text-sm text-base-content/60 mb-5">
                    Escolha o status que reflete o que realmente aconteceu com este{' '}
                    {entityLabel}. Nada além do status muda: nenhuma cobrança é gerada
                    ou cancelada, nenhum repasse é disparado e o paciente não é avisado.
                </p>

                <div className="flex flex-col gap-2">
                    {options.map(option => (
                        <label
                            key={option.value}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selected === option.value
                                    ? 'border-secondary bg-secondary/10'
                                    : 'border-base-300 hover:bg-base-200/60'
                            }`}
                        >
                            <input
                                type="radio"
                                name="appointment-status"
                                className="radio radio-sm radio-secondary"
                                value={option.value}
                                checked={selected === option.value}
                                onChange={() => setSelected(option.value)}
                            />
                            <span className="font-medium">{option.label}</span>
                            {option.value === currentStatus && (
                                <span className="badge badge-sm badge-ghost ml-auto">
                                    Atual
                                </span>
                            )}
                        </label>
                    ))}
                </div>

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={saving || selected === currentStatus}
                        onClick={() => onConfirm(selected)}
                    >
                        {saving && <span className="loading loading-spinner loading-sm" />}
                        Salvar status
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
